import { DialogClasses, Dialog } from '@mui/material';
import { ForcedTheme } from 'components/ForcedTheme/ForcedTheme';
import { getMapDialogCommonStyles } from 'features/mapping/mapping.utils';
import { ReactNode, useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';
import { MAP_ENTITY_DRAWER_WIDTH } from 'features/mapping/components/MapEntityDrawer';
import { useMapEntityDrawerState } from 'features/mapping/hooks/useMapEntityDrawerState';
import { useSearchDrawerState } from 'state/useSearchDrawerState';
import { SEARCH_DRAWER_WIDTH } from '../../../../../constants';

type TimelineModalProps = {
  children: ReactNode;
  open?: boolean;
  /** Portal mount node. Defaults to `document.body`. */
  container?: HTMLElement | null;
};

const useStyles = makeStyles<{
  isDrawerOpen: boolean;
  isSearchDrawerOpen: boolean;
  minSnapPct: number;
}>()((theme, { isDrawerOpen, isSearchDrawerOpen, minSnapPct }) => {
  const baseStyles = getMapDialogCommonStyles(theme, {
    isDrawerEntitySelected: isDrawerOpen,
    isSearchDrawerOpen,
    minWidth: 362,
    minSnapPct: isDrawerOpen ? minSnapPct : 0,
    isDarkShell: true,
  });

  const drawerWidth = (() => {
    if (isDrawerOpen) {
      return MAP_ENTITY_DRAWER_WIDTH;
    }
    if (isSearchDrawerOpen) {
      return SEARCH_DRAWER_WIDTH;
    }
    return 0;
  })();

  return {
    ...baseStyles,
    root: {
      ...baseStyles.root,
      // Position below drawers on all screen sizes (mobileStepper is lowest MUI z-index at 1000)
      zIndex: theme.zIndex.mobileStepper,
      // On desktop and tablet, apply horizontal offset when drawer or search is open
      [theme.breakpoints.up('tablet')]: {
        transform: drawerWidth
          ? `translateX(-${drawerWidth / 2}px)`
          : 'translateX(0)',
        transition: 'transform 225ms cubic-bezier(0, 0, 0.2, 1) 0ms',
      },
      // On mobile, position at true bottom to cover search bar
      [theme.breakpoints.down('tablet')]: {
        bottom: '0px !important',
        transform: 'none',
      },
    },
    container: {
      ...baseStyles.container,
      [theme.breakpoints.down('tablet')]: {
        width: '100% !important',
        minWidth: 'unset !important',
      },
    },
    paper: {
      ...baseStyles.paper,
      [theme.breakpoints.up('tablet')]: {
        bottom: theme.spacing(2),
        borderRadius: 12,
      },
      // On desktop, set fixed width
      [theme.breakpoints.up('laptop')]: {
        width: '590px !important',
        maxWidth: '590px !important',
      },
      // On tablet, reduce width to fit better
      [theme.breakpoints.between('tablet', 'laptop')]: {
        width: '450px !important',
        maxWidth: '450px !important',
        minWidth: '450px !important',
      },
      // Full width on mobile, at bottom covering search bar
      [theme.breakpoints.down('tablet')]: {
        width: '100% !important',
        maxWidth: '100% !important',
        minWidth: 'unset !important',
        margin: '0 !important',
        bottom: '0 !important',
        borderRadius: `${Number(theme.shape.borderRadius) * 2}px ${Number(theme.shape.borderRadius) * 2}px 0 0`,
      },
    },
  };
});

export const TimelineModal = (
  props: TimelineModalProps,
): JSX.Element | null => {
  const { children, open = true, container } = props;
  const { open: isDrawerOpen, minSnapPct } = useMapEntityDrawerState();
  const { open: isSearchDrawerOpen } = useSearchDrawerState();

  const { classes } = useStyles({
    isDrawerOpen,
    isSearchDrawerOpen,
    minSnapPct,
  });

  const dialogClasses = useMemo(
    (): Partial<DialogClasses> => ({
      root: classes.root,
      container: classes.container,
      paper: classes.paper,
    }),
    [classes.root, classes.container, classes.paper],
  );

  if (!open) {
    return null;
  }

  return (
    <Dialog
      open={open}
      classes={dialogClasses}
      hideBackdrop
      container={container ?? document.body}
      slotProps={{
        paper: {
          onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
          onTouchStart: (e: React.TouchEvent) => e.stopPropagation(),
        },
      }}
    >
      <ForcedTheme mode="dark">{children}</ForcedTheme>
    </Dialog>
  );
};
